import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LabelForDirective } from 'app/shared/form-utils/label-for.directive';
import { ValueType } from 'app/shared/pipes/value.pipe';
import { isEqual } from 'app/shared/utils';
import { compareFn } from '../models';
import { PipesModule } from '../pipes';
import { UiModule } from '../ui';
import { AveragePipe } from './average.pipe';
import { StickyElementDirective, StickyTargetDirective } from './sticky.directive';
import { Direction, HeaderState, TableHeaderComponent } from './table-header.component';


export type Column = {
    key: string;
    title: string;
    selected: boolean;
    abbr?: string;
    type?: ValueType;
    tip?: string;
}

export type Averages = { [columnKey: string]: ValueType; }

export type OrderChange<T> = {
    orderBy: string;
    orderReverse: boolean;
    listings: T[];
}

export type RemoveListing<T> = {
    removed: T;
    listings: T[];
}


type Selection = Record<string, boolean>;


@Component({
    selector: 'comparables-table',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        LabelForDirective,
        UiModule,
        PipesModule,
        TableHeaderComponent,
        StickyTargetDirective,
        StickyElementDirective,
        AveragePipe,
    ],
    styles: [
        `
        @media screen {
            .first-col div {
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 1em;
            }
            .f-6 {
                font-size: 6px;
            }
            .f-7 {
                font-size: 7px;
            }
            .f-8 {
                font-size: 8px;
            }
            .f-9 {
                font-size: 9px;
            }
            .f-10 {
                font-size: 10px;
            }
            .f-11 {
                font-size: 11px;
            }
            .f-12 {
                font-size: 12px;
            }
            .f-13 {
                font-size: 13px;
            }
            .f-14 {
                font-size: 14px;
            }
        }
        @media screen, print {
            .averages {
                font-weight: bold;
                background-color: transparent !important;
            }
            .averages .title {
                display: inline-block;
                width: 0;
                overflow: visible;
                word-wrap: normal;
            }
        }
        @media print {
            /* .table {
                overflow-wrap: break-word;
            } */
            .first-col {
                width: 0;
            }
            th, td {
                font-size: inherit;
                padding: 2px 4px;
                word-wrap: break-word;
            }
            .f-6 {
                font-size: 4pt;
            }
            .f-7 {
                font-size: 5pt;
            }
            .f-8 {
                font-size: 6pt;
            }
            .f-9 {
                font-size: 7pt;
            }
            .f-10 {
                font-size: 8pt;
            }
            .f-11 {
                font-size: 9pt;
            }
            .f-12 {
                font-size: 10pt;
            }
            .f-13 {
                font-size: 11pt;
            }
            .f-14 {
                font-size: 12pt;
            }
        }
        `,
    ],
    template: `
        <div class="table-responsive">
            <table [class]="'table table-striped table-hover table-sm f-' + fontSize" stickyTarget>
                <thead>
                    <tr stickyElement class="bg-white">
                        <th class="first-col">
                            <div class="print-hidden">
                                <input id="ctAll" type="checkbox" [ngModel]="isAllSelected" (click)="selectAll()">
                                <label for="ctAll" class="form-check-label">All</label>
                            </div>
                        </th>
                        <th class="text-right">#</th>
                        @for (column of columns; track column.key) {
                            @if (column.selected) {
                                <th [class.text-right]="isRightAligned(column.type)">
                                    <table-header
                                        [orderByState]="getOrderState(column.key)"
                                        (sort)="setOrder(column.key, $event)">
                                        @if (column.abbr) {
                                            <abbr [title]="column.abbr">{{ column.title }}</abbr>
                                        }
                                        @else {
                                            {{ column.title }}
                                        }
                                    </table-header>
                                </th>
                            }
                        }
                    </tr>
                </thead>
                <tbody>
                    @for (listing of listings; track listing.id) {
                        <tr>
                            <td class="first-col">
                                <div class="print-hidden">
                                    <input type="checkbox" [ngModel]="selectedListings[listing.id]" (click)="selectListing(listing.id)">
                                    <button class="btn btn-secondary btn-sm" (click)="remove(listing)">
                                        <i class="fa fa-trash-o"></i>
                                    </button>
                                </div>
                            </td>
                            <td class="text-right">{{ $index + 1 }}</td>
                            @for (column of columns; track column.key) {
                                @if (column.selected) {
                                    <td [class.text-right]="isRightAligned(column.type)" [innerHTML]="listing[column.key] | value: column.type"></td>
                                }
                            }
                        </tr>
                    }
                    @if (averages) {
                        <tr class="averages">
                            <th colspan="2">
                                <span class="title">Averages</span>
                            </th>
                            @for (column of columns; track column.key) {
                                @if (column.selected) {
                                    <td [class.text-right]="isRightAligned(averages[column.key])">
                                        @if (averages[column.key]) {
                                            {{ listings | average: column.key | value: averages[column.key] }}
                                        }
                                    </td>
                                }
                            }
                        </tr>
                    }
                </tbody>
            </table>
        </div>
    `,
})
export class ComparablesTableComponent<T extends { id: string; [_: string]: any }> {

    @Input() columns: Column[];

    @Input() fontSize = 12;

    private _listings: T[];

    @Input() set listings(listings: T[]) {
        this._listings = listings;
        this.isAllSelected = isEqual(this.selectedListings, this.getAllSelection());
    }

    get listings() {
        return this._listings;
    }

    @Input() averages: Averages;

    @Input() orderBy: string;

    @Input() orderReverse = false;

    private _selectedListings: Selection = {};

    isAllSelected = false;

    @Input() set selectedListings(selection: Selection) {
        this._selectedListings = selection;
        this.isAllSelected = isEqual(selection, this.getAllSelection());
    }

    get selectedListings() {
        return this._selectedListings;
    }

    @Output() orderChange = new EventEmitter<OrderChange<T>>();

    @Output() removeListing = new EventEmitter<RemoveListing<T>>();

    @Output() selectListings = new EventEmitter<Selection>();

    isRightAligned(valueType?: ValueType) {
        return valueType === 'number' || valueType === 'number2' || valueType === 'price' || valueType === 'price2';
    }

    setOrder(columnKey: string, direction: Direction) {
        const orderReverse = direction === 'desc';
        this.orderChange.emit({
            orderBy: columnKey as string,
            orderReverse: orderReverse,
            listings: [...this.listings].sort((a, b) => compareFn(a, b, columnKey as keyof T, orderReverse)),
        });
    }

    getOrderState(columnKey: string): HeaderState {
        if (this.orderBy !== columnKey) {
            return 'inactive';
        }
        return this.orderReverse ? 'desc' : 'asc';
    }

    remove(listing: T) {
        if (this.selectedListings[listing.id]) {
            this.selectListing(listing.id);
        }
        this.removeListing.emit({
            removed: listing,
            listings: this.listings.filter(l => l !== listing),
        });
    }

    selectListing(id: string) {
        const filterFalsy = (x: object) => Object.fromEntries(Object.entries(x).filter(([_, v]) => !!v));
        const selection = {
            ...this.selectedListings,
            [id]: !this.selectedListings[id],
        };
        this.selectListings.emit(filterFalsy(selection));
    }

    protected getAllSelection() {
        const reducer = (selection: Selection, listing: T) => {
            selection[listing.id] = true;
            return selection;
        };
        return this.listings.reduce(reducer, {});
    }

    selectAll() {
        this.selectListings.emit(this.isAllSelected ? {} : this.getAllSelection());
    }
}
